// src/pages/Login.tsx
import "../styles/utilities.css";
import React, {useEffect, useState} from "react";
import {Flex, Input, InputGroup, Text, VStack} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";
import {FaUser} from "react-icons/fa";
import {FcGoogle} from "react-icons/fc";
import {IoEye, IoEyeOff} from "react-icons/io5";
import {MdLock} from "react-icons/md";
import AppSpinner from "../components/AppSpinner.tsx";
import MicrosoftLogo from "../components/icons/MicrosoftLogo.tsx";
import Logo from "../components/Logo.tsx";
import {PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL} from "../models/constants/constants.ts";
import {
  consumeMicrosoftRedirect,
  isMicrosoftReturn,
  isProviderConfigured,
  preloadGoogle,
  preloadMicrosoft,
} from "../services/oauthProviders.ts";
import type {OAuthProvider} from "../services/oauthProviders.ts";
import useAuthStore from "../store/useAuthStore.ts";
import {providerUnavailableMessage} from "../utils/loginErrors.ts";
import {getGreeting} from "../utils/utils.ts";

type SdkStatus = "loading" | "ready" | "failed";

const PROVIDERS: OAuthProvider[] = ["google", "microsoft"];

const PROVIDER_LABEL: Record<OAuthProvider, string> = {
  google: "Continue with Google",
  microsoft: "Continue with Microsoft",
};

const PROVIDER_LOGO: Record<OAuthProvider, React.ReactNode> = {
  google: <FcGoogle size={20} aria-hidden="true"/>,
  microsoft: <MicrosoftLogo/>,
};

const preloadProvider = (provider: OAuthProvider): Promise<void> =>
  provider === "google" ? preloadGoogle() : preloadMicrosoft();

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState(import.meta.env.VITE_USERNAME || "");
  const [password, setPassword] = useState(import.meta.env.VITE_PASSWORD || "");
  const [showPassword, setShowPassword] = useState(false);
  const [sdk, setSdk] = useState<Record<OAuthProvider, SdkStatus>>({google: "loading", microsoft: "loading"});
  const {loading, login, loginWithProvider, completeProviderLogin, error, isAuthenticated, activeProvider} =
    useAuthStore();
  const navigate = useNavigate();

  // `loading` alone is not enough: App.tsx's initializeAuth() runs after this mount effect and
  // synchronously sets loading=false when no Glix token exists, which would unlock the form in
  // the middle of the Microsoft return leg. activeProvider is the lock the bootstrap never touches.
  const busy = loading || activeProvider !== null;
  const configuredProviders = PROVIDERS.filter(isProviderConfigured);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Zustand actions are stable references, so this effect runs once on mount.
  useEffect(() => {
    PROVIDERS.filter(isProviderConfigured).forEach((provider) => {
      preloadProvider(provider).then(
        () => setSdk((previous) => ({...previous, [provider]: "ready"})),
        () => setSdk((previous) => ({...previous, [provider]: "failed"}))
      );
    });
    if (isProviderConfigured("microsoft") && isMicrosoftReturn()) {
      void completeProviderLogin("microsoft", consumeMicrosoftRedirect());
    }
  }, [completeProviderLogin]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    void login(username, password);
  };

  return (
    <Flex className="w-full h-full flex flex-col">
      <Flex>
        <Logo/>
      </Flex>
      <Flex className="flex-1 w-full items-center justify-center">
        <VStack className="login-column" spacing={8} data-testid="login-column">
          <Text as="h1" className="text-3xl font-bold text-center">
            👋 {getGreeting()}, Welcome to Glix!
          </Text>

          {configuredProviders.length > 0 && (
            <VStack className="w-full" spacing={7}>
              <VStack className="w-full" spacing={2.5}>
                {configuredProviders.map((provider) => (
                  <button
                    key={provider}
                    type="button"
                    className="login-provider-button"
                    onClick={() => {
                      void loginWithProvider(provider);
                    }}
                    disabled={busy || sdk[provider] !== "ready"}
                    aria-busy={sdk[provider] === "loading"}
                  >
                    {PROVIDER_LOGO[provider]}
                    {PROVIDER_LABEL[provider]}
                    {activeProvider === provider && <AppSpinner size="sm"/>}
                  </button>
                ))}
                {configuredProviders
                  .filter((provider) => sdk[provider] === "failed")
                  .map((provider) => (
                    <Text key={provider} role="status" className="text-sm text-center text-n-400">
                      {providerUnavailableMessage(provider)}
                    </Text>
                  ))}
              </VStack>
              <div className="login-divider" role="separator">
                <span className="login-divider-line"/>
                <span>or</span>
                <span className="login-divider-line"/>
              </div>
            </VStack>
          )}

          <form onSubmit={handleSubmit} noValidate className="w-full">
            <VStack className="w-full" spacing={3}>
              <InputGroup className="login-input-wrapper gap-2">
                <FaUser className="text-loginInputsText-500 text-xl ml-3" aria-hidden="true"/>
                <Input
                  type="text"
                  name="username"
                  variant="unstyled"
                  className="login-input-field"
                  placeholder="Username or email"
                  aria-label="Username or email"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={busy}
                />
              </InputGroup>

              <InputGroup className="login-input-wrapper gap-2">
                <MdLock className="text-loginInputsText-500 text-xl ml-3" aria-hidden="true"/>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  variant="unstyled"
                  className="login-input-field"
                  placeholder="Password"
                  aria-label="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="login-icon-button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={busy}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IoEyeOff/> : <IoEye/>}
                </button>
              </InputGroup>

              {error && (
                <Text role="alert" fontSize="md" textAlign="center" color="red" maxW="sm">
                  {error}
                </Text>
              )}

              <button
                type="submit"
                className="login-button disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={busy}
              >
                {loading && activeProvider === null ? <AppSpinner size="lg"/> : "Continue"}
              </button>
            </VStack>
          </form>

          <Text className="login-footer">
            By continuing, you agree to Glix.ai Company the{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">Terms of Service</a>
            {" "}and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>.
          </Text>
        </VStack>
      </Flex>
    </Flex>
  );
};

export default LoginPage;
